import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';

import type { ListaDeModelos, ModeloDoProvedor } from './api';
import { filtrarModelos, informaPreco, precoDoModelo, rodapeDaLista, rotuloDasEntradas, textoDoContexto } from './configuracao-da-ia';
import { Recado } from './Pecas';

/**
 * O campo de modelo: um combobox que busca na lista do provedor e aceita o
 * identificador escrito à mão (modelo novo, ou lista que não veio).
 *
 * A lista é pedida PELA API (`GET /plataforma/configuracoes/ia/modelos`): o
 * navegador nunca fala com o provedor, e a chave nunca sai do servidor. Ela é
 * uma só para os três campos, e quem a pede é a tela; aqui chega o estado dela.
 *
 * ACESSÍVEL PELO TECLADO (regra 8): o campo é `role=combobox`, as setas andam
 * na lista pelo `aria-activedescendant` sem tirar o foco do campo, Enter
 * escolhe e Esc fecha. O anel de foco é o do campo, intacto, e o item ativo
 * ganha o mesmo contorno laranja.
 *
 * MENU ANCORADO (R01, p.29): colado ao campo a 8 px (`--menu-ancorado-
 * distancia`), e acima dele quando embaixo não cabe. Vai para o `body` por
 * portal, em posição fixa medida no campo: dentro do formulário em grade, que
 * é um container, a lista ficaria presa ao formulário e por baixo da barra de
 * ações fixa.
 */

export type EstadoDaLista =
  | { readonly tipo: 'endereco-invalido' }
  | { readonly tipo: 'nao-lista' }
  | { readonly tipo: 'carregando' }
  | {
      readonly tipo: 'erro';
      readonly erro: { readonly erro: string; readonly comoResolver: string };
      readonly recarregar: () => void;
    }
  | { readonly tipo: 'pronta'; readonly lista: ListaDeModelos };

/* A altura maxima da lista aberta, multipla de 4. A distancia ao campo vem do
   token, lida na hora: o valor nao se repete aqui. */
const ALTURA_MAXIMA = 400;
const LARGURA_MINIMA = 360;

interface Posicao {
  readonly left: number;
  readonly width: number;
  readonly maxHeight: number;
  readonly top?: number;
  readonly bottom?: number;
}

function distanciaDoMenu(): number {
  const lida = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--menu-ancorado-distancia'));
  return Number.isFinite(lida) ? lida : 8;
}

/** Abaixo do campo; acima quando embaixo não cabe e em cima há mais espaço. */
function posicaoJuntoAo(campo: HTMLElement): Posicao {
  const r = campo.getBoundingClientRect();
  const d = distanciaDoMenu();
  const largura = Math.min(Math.max(r.width, LARGURA_MINIMA), window.innerWidth - 2 * d);
  const esquerda = Math.max(d, Math.min(r.left, window.innerWidth - largura - d));
  const embaixo = window.innerHeight - r.bottom - 2 * d;
  const emCima = r.top - 2 * d;
  const paraCima = embaixo < ALTURA_MAXIMA && emCima > embaixo;
  const altura = Math.max(Math.min(ALTURA_MAXIMA, paraCima ? emCima : embaixo), 0);
  return paraCima
    ? { left: esquerda, width: largura, maxHeight: altura, bottom: window.innerHeight - r.top + d }
    : { left: esquerda, width: largura, maxHeight: altura, top: r.bottom + d };
}

export function SeletorDeModelo({
  rotulo,
  valor,
  aoMudar,
  tipo,
  lista,
  ajuda,
  validar,
  opcional = false,
  desligado = false,
  avisos = [],
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  /** `vetor` usa a lista de modelos de vetor e mostra só o preço de entrada. */
  readonly tipo: 'resposta' | 'vetor';
  readonly lista: EstadoDaLista;
  readonly ajuda: string;
  /** Roda AO SAIR do campo (p.16). */
  readonly validar: (valor: string) => string | null;
  readonly opcional?: boolean;
  readonly desligado?: boolean;
  /** Frases neutras, abaixo do campo: não impedem salvar. */
  readonly avisos?: readonly string[];
}) {
  const id = useId();
  const idDoMenu = `${id}-menu`;
  const idDaOpcao = (indice: number) => `${id}-opcao-${String(indice)}`;
  const campo = useRef<HTMLInputElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [aberto, setAberto] = useState(false);
  /* O termo da busca é o que a pessoa DIGITOU desde a última escolha. Com um
     modelo já escolhido, abrir a lista mostra todos, e não só ele. */
  const [termo, setTermo] = useState<string | null>(null);
  const [ativo, setAtivo] = useState(-1);
  const [erro, setErro] = useState<string | null>(null);
  const [posicao, setPosicao] = useState<Posicao | null>(null);

  const modelos: readonly ModeloDoProvedor[] =
    lista.tipo === 'pronta' ? (tipo === 'vetor' ? lista.lista.modelosDeVetor : lista.lista.modelos) : [];
  const filtrados = filtrarModelos(modelos, termo ?? '');
  const opcoes = filtrados.visiveis;
  const escolhido = valor.trim();

  useLayoutEffect(() => {
    if (!aberto) return;
    const alvo = campo.current;
    if (!alvo) return;
    setPosicao(posicaoJuntoAo(alvo));
    function remedir(evento: Event) {
      /* A rolagem da PRÓPRIA lista não move o campo. */
      if (evento.target instanceof Node && menu.current?.contains(evento.target)) return;
      if (alvo) setPosicao(posicaoJuntoAo(alvo));
    }
    window.addEventListener('scroll', remedir, true);
    window.addEventListener('resize', remedir);
    return () => {
      window.removeEventListener('scroll', remedir, true);
      window.removeEventListener('resize', remedir);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto || ativo < 0) return;
    document.getElementById(`${id}-opcao-${String(ativo)}`)?.scrollIntoView({ block: 'nearest' });
  }, [aberto, ativo, id]);

  function abrir() {
    setAberto(true);
    setAtivo(termo === null ? opcoes.findIndex((m) => m.id === escolhido) : -1);
  }

  function fechar() {
    setAberto(false);
    setAtivo(-1);
    setPosicao(null);
  }

  function escolher(modelo: string) {
    aoMudar(modelo);
    setTermo(null);
    setErro(null);
    fechar();
  }

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>) {
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        if (!aberto) abrir();
        else setAtivo((a) => (opcoes.length === 0 ? -1 : (a + 1) % opcoes.length));
        return;
      case 'ArrowUp':
        evento.preventDefault();
        if (!aberto) abrir();
        else setAtivo((a) => (opcoes.length === 0 ? -1 : a <= 0 ? opcoes.length - 1 : a - 1));
        return;
      case 'Enter': {
        if (!aberto) return;
        evento.preventDefault();
        const alvo = opcoes[ativo];
        /* Sem item ativo, Enter fica com o que foi escrito: identificador
           fora da lista é permitido, com o aviso abaixo do campo. */
        if (alvo) escolher(alvo.id);
        else fechar();
        return;
      }
      case 'Escape':
        if (!aberto) return;
        evento.preventDefault();
        fechar();
        return;
      default:
        return;
    }
  }

  const precoInformado = informaPreco(modelos);
  const rodape = lista.tipo === 'pronta' ? rodapeDaLista(filtrados) : null;
  const semChave = lista.tipo === 'pronta' && !lista.lista.comChave;

  function conteudo() {
    switch (lista.tipo) {
      case 'endereco-invalido':
        return <p className="txt-apoio p-3 text-tinta-secundaria">Preencha um endereço válido para carregar a lista.</p>;
      case 'nao-lista':
        return (
          <p className="txt-apoio p-3 text-tinta-secundaria">
            Este protocolo não lista modelos. Escreva o identificador exato, como está no guia do provedor.
          </p>
        );
      case 'carregando':
        /* Carregando (p.32) na forma da opção — o nome, o identificador
           embaixo e o preço à direita —, e não a frase em cima de barras. */
        return (
          <Carregando oQue="Carregando os modelos do provedor" className="flex flex-col">
            {['w-1/2', 'w-2/5', 'w-3/5'].map((largura, linha) => (
              <div
                key={linha}
                className="flex items-start justify-between gap-4 border-b border-borda-sutil px-3 py-2 last:border-b-0"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <Barra className={`my-1 h-3 ${largura}`} />
                  <Barra className="my-1 h-3 w-3/4" />
                </span>
                <Barra className="my-1 h-3 w-16" />
              </div>
            ))}
          </Carregando>
        );
      case 'erro':
        return (
          <div className="flex flex-col items-start gap-2 p-3">
            <Recado erro={lista.erro} />
            <Botao nivel="terciario" onClick={lista.recarregar}>
              Carregar de novo
            </Botao>
          </div>
        );
      case 'pronta':
        if (modelos.length === 0) {
          return (
            <p className="txt-apoio p-3 text-tinta-secundaria">
              O provedor não devolveu modelos para este campo. Escreva o identificador exato, como está no guia do provedor.
            </p>
          );
        }
        if (opcoes.length === 0) {
          return (
            <div className="flex flex-col items-start gap-2 p-3">
              <p className="txt-apoio text-tinta-secundaria">{`Nenhum modelo com “${termo ?? ''}”.`}</p>
              <Botao
                nivel="terciario"
                onClick={() => {
                  setTermo(null);
                  setAtivo(-1);
                }}
              >
                Limpar busca
              </Botao>
            </div>
          );
        }
        return (
          <ul role="listbox" aria-label={rotulo} className="flex flex-col">
            {opcoes.map((m, indice) => {
              const preco = precoDoModelo(m, tipo, precoInformado);
              const detalhes = [rotuloDasEntradas(m.entradas), textoDoContexto(m.contexto)].filter(Boolean).join(' · ');
              return (
                <li
                  key={m.id}
                  id={idDaOpcao(indice)}
                  role="option"
                  aria-selected={m.id === escolhido}
                  onMouseMove={() => {
                    if (ativo !== indice) setAtivo(indice);
                  }}
                  onClick={() => {
                    escolher(m.id);
                  }}
                  className={`flex cursor-pointer items-start justify-between gap-4 border-b border-borda-sutil px-3 py-2 last:border-b-0 ${
                    m.id === escolhido ? 'bg-selecao' : ''
                  } ${indice === ativo ? 'outline-2 -outline-offset-2 outline-marca' : ''}`}
                >
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="txt-corpo text-tinta">{m.nome}</span>
                    <span className="mono txt-apoio break-all text-tinta-secundaria">{m.id}</span>
                    {detalhes ? <span className="txt-apoio text-tinta-secundaria">{detalhes}</span> : null}
                  </span>
                  <span className="flex shrink-0 flex-col items-end text-right">
                    <span className="txt-apoio tabular-nums text-tinta">{preco.valor}</span>
                    {preco.unidade ? <span className="txt-apoio text-tinta-secundaria">{preco.unidade}</span> : null}
                  </span>
                </li>
              );
            })}
          </ul>
        );
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Campo id={id} rotulo={rotulo} opcional={opcional} ajuda={ajuda} erro={erro}>
        <input
          id={id}
          ref={campo}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={aberto}
          {...(aberto ? { 'aria-controls': idDoMenu } : {})}
          {...(aberto && ativo >= 0 ? { 'aria-activedescendant': idDaOpcao(ativo) } : {})}
          {...(erro ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {})}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          maxLength={200}
          disabled={desligado}
          value={valor}
          className="controle mono w-full"
          onClick={() => {
            if (!aberto) abrir();
          }}
          onChange={(evento) => {
            setErro(null);
            setTermo(evento.target.value);
            setAtivo(-1);
            setAberto(true);
            aoMudar(evento.target.value);
          }}
          onKeyDown={aoTeclar}
          onBlur={() => {
            fechar();
            setTermo(null);
            setErro(validar(valor));
          }}
        />
      </Campo>

      {avisos.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {avisos.map((aviso) => (
            <li key={aviso} className="txt-apoio text-pretty text-tinta-secundaria">
              {aviso}
            </li>
          ))}
        </ul>
      ) : null}

      {aberto && posicao
        ? createPortal(
            <div
              ref={menu}
              id={idDoMenu}
              /* O clique na lista não tira o foco do campo: a escolha, a busca
                 e o "Carregar de novo" acontecem com o combobox focado. */
              onMouseDown={(evento) => {
                evento.preventDefault();
              }}
              className="cartao fixed z-40 flex flex-col overflow-hidden"
              style={posicao}
            >
              <div className="min-h-0 flex-auto overflow-auto">{conteudo()}</div>
              {rodape || semChave ? (
                <div className="flex flex-col gap-1 border-t border-borda-sutil bg-rebaixada px-3 py-2">
                  {rodape ? <span className="txt-apoio text-tinta-secundaria">{rodape}</span> : null}
                  {semChave ? <span className="txt-apoio text-tinta-secundaria">Lista pública do provedor, pedida sem a chave.</span> : null}
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
